// static/js/components/ui/ActivityFeedback.jsx

/**
 * Component: ActivityFeedback
 * 
 * This React component provides a visual feedback mechanism for activities in the application,
 * particularly useful for indicating what the AI assistant is doing.
 * The component displays a spinner icon next to a text message, making it ideal for streaming
 * processes or any activity requiring user patience.
 * 
 * Props:
 *  - message (String): The message to display alongside the loading spinner.
 *  - isStreaming (Boolean): A flag to indicate if the streaming process is active, affecting
 *    the internal logging behavior. Default is false.
 * 
 * Behavior:
 *  - The component renders a spinning loader icon followed by a provided message if the
 *    `message` prop is not empty.
 *  - If the `message` prop is empty, the component renders nothing, effectively staying
 *    invisible.
 *  - Includes debug logging for component rendering and cases where no message is provided.
 *    (Note: Debug logs are marked for removal and should be cleaned up post-debugging phase.)
 * 
 * Usage:
 *  The component can be used in parts of the application where feedback for ongoing operations
 *  is required to inform the user about the state of background activities.
 * 
 * Styling:
 *  - The component utilizes Tailwind CSS for styling, ensuring it fits seamlessly within a
 *    Tailwind-enabled project environment.
 *  - Accessibility is enhanced with 'aria-live' set to 'polite', allowing screen readers to
 *    announce changes when they occur without interrupting the user.
 */

import React from 'react';
import { Loader } from 'lucide-react';

const ActivityFeedback = ({ message, isStreaming = false }) => {
  console.log('[DEBUG ACTIVITY STATUS] ActivityFeedback render:', { message, isStreaming }); // TODO : rm logging

  // Don't render if no message
  if (!message) {
    console.log('[DEBUG ACTIVITY STATUS] No message to display'); // TODO : rm logging
    return null;
  }

  return (
    <div 
      className="flex items-center space-x-2 text-sm text-primary-text/70"
      aria-live="polite"
    >
      <Loader className="animate-spin h-4 w-4" />
      <span>{message}</span>
    </div>
  );
};

export default ActivityFeedback;
